import { useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { IconChevronRight, IconDotsVertical } from '@rossum/ui/icons/tabler';
import { ListItemText, Menu, MenuItem, SvgIcon } from '@rossum/ui/material';
import { GridActionsCellItem } from '@rossum/ui/x-data-grid-pro';
import {
  canHaveAttachments,
  isDeletable,
  isPostponable,
  isPurgeable,
  isReAutomatable,
  isReExtractable,
  isReprocessable,
} from '../../../redux/modules/annotation/helpers';
import {
  documentHistoryEnabledSelector,
  reautomateEnabledSelector,
} from '../../../redux/modules/organizationGroup/selectors';
import {
  canUserPurgeSelector,
  isUserAnnotatorBetaSelector,
  isUserAtLeastManager,
  isUserViewer,
} from '../../../redux/modules/user/selectors';
import { SnakeCaseStatus } from '../../../types/annotationStatus';
import { useCustomRelationsConfig } from '../../custom-relations/hooks/useCustomRelationsConfig';
import { SelectAnnotationParams } from '../../document-list-base/supportedAnnotationViews';
import { attachmentsFeatureSelector } from '../../pricing/selectors';
import { getExportMutationState } from '../annotation-actions/config';
import { usePermittedActions } from '../annotation-actions/hooks/usePermittedActions';
import { ExportMenu } from '../export-menu/ExportMenu';
import { useAnnotationActionsContext } from '../selection-panel/AnnotationActionContext';
import { ReProcessMenu } from '../selection-panel/ReProcessMenu';
import { moveToReviewTabs } from '../statuses/tabs';
import { AllDocsAnnotation } from '../types';
import { BatchLabels } from './BatchLabels';

type RowActionsProps = {
  annotation: AllDocsAnnotation;
  handleSelectAnnotation: (params: SelectAnnotationParams) => void;
};

const resolveActionsOrder = ({
  status,
  canDownload,
  canDelete,
  canPurge,
  labelsEnabled,
  isAttachmentsEnabled,
  isCustomRelationsEnabled,
  trainingToggleEnabled,
  showMoveToReview,
  isReAutomateEnabled,
  isDocumentHistoryEnabled,
}: {
  status: SnakeCaseStatus;
  canDownload: boolean;
  canDelete: boolean;
  canPurge: boolean;
  labelsEnabled: boolean;
  isAttachmentsEnabled: boolean;
  isCustomRelationsEnabled: boolean;
  trainingToggleEnabled: boolean;
  showMoveToReview: boolean;
  isReAutomateEnabled: boolean;
  isDocumentHistoryEnabled: boolean;
}) =>
  R.pipe(
    [
      showMoveToReview ? 'moveToReview' : 'validate',
      isPostponable(status) && 'postpone',
      labelsEnabled && 'label',
      'move',
      canDownload && 'export',
      trainingToggleEnabled && 'toggleTraining',
      isReprocessable(status) && 'reprocess',
      isReExtractable({ status }) && 'reExtract',
      isReAutomateEnabled && isReAutomatable({ status }) && 'reAutomate',
      isDocumentHistoryEnabled && 'documentHistory',
      isDeletable(status) && canDelete && 'delete',
      isPurgeable(status) && canPurge && 'purge',
      isAttachmentsEnabled && canHaveAttachments(status) && 'addAttachment',
      // BE puts no status constraint on custom relations; blocking not-yet-imported
      // and purged documents is a FE-only choice mirroring the attachment UX.
      isCustomRelationsEnabled &&
        canHaveAttachments(status) &&
        'createRelation',
    ] as const,
    R.filter(R.isTruthy)
  );

const RowActions = ({
  annotation,
  handleSelectAnnotation,
}: RowActionsProps) => {
  const { status, content } = annotation;

  const { annotationActions, activeTabKey, queueExportSettings } =
    useAnnotationActionsContext();
  const showMoveToReview =
    activeTabKey !== null && moveToReviewTabs.has(activeTabKey);

  const isViewer = useSelector(isUserViewer);
  const isAtLeastManager = useSelector(isUserAtLeastManager);
  const canUserPurge = useSelector(canUserPurgeSelector);
  const isAnnotatorLimited = useSelector(isUserAnnotatorBetaSelector);
  const intl = useIntl();

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const { permittedActions, isLabelsEnabled } = usePermittedActions();

  const isAttachmentsEnabled = useSelector(attachmentsFeatureSelector);
  const isReAutomateEnabled = useSelector(reautomateEnabledSelector);
  const isDocumentHistoryEnabled = useSelector(documentHistoryEnabledSelector);
  const { isEnabled: isCustomRelationsEnabled } = useCustomRelationsConfig();

  const orderedActions = resolveActionsOrder({
    status,
    canDownload: !isAnnotatorLimited && !!content,
    canDelete: !isViewer,
    labelsEnabled: isLabelsEnabled,
    isAttachmentsEnabled,
    isCustomRelationsEnabled,
    canPurge: canUserPurge,
    trainingToggleEnabled:
      isAtLeastManager && status !== 'deleted' && status !== 'purged',
    showMoveToReview,
    isReAutomateEnabled,
    isDocumentHistoryEnabled,
  });

  const allowedActions = orderedActions.filter(action =>
    permittedActions.includes(action)
  );
  const canReExtract = allowedActions.includes('reExtract');
  const canReAutomate = allowedActions.includes('reAutomate');

  return (
    <>
      <GridActionsCellItem
        icon={
          <SvgIcon fontSize="large">
            <IconDotsVertical />
          </SvgIcon>
        }
        label="Options"
        data-cy="all-document-dashboard-single-row-actions"
        data-id={annotation.id}
        onClick={(e: React.MouseEvent) => {
          setAnchorEl(e.currentTarget);
        }}
      />

      {annotationActions ? (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {allowedActions.map(action => {
            if (action === 'label') {
              return (
                <BatchLabels
                  key={action}
                  selectedAnnotations={[annotation]}
                  onLabel={annotationActions.label}
                  nested
                  onParentClose={() => setAnchorEl(null)}
                >
                  {({ isOpen }) => (
                    <MenuItem
                      selected={isOpen}
                      data-cy={`single-row-action-${action}`}
                    >
                      <ListItemText>
                        {intl.formatMessage({
                          id: 'containers.annotationList.selectionPanel.buttons.label',
                        })}
                      </ListItemText>
                      <SvgIcon fontSize="small" sx={{ ml: 1 }}>
                        <IconChevronRight />
                      </SvgIcon>
                    </MenuItem>
                  )}
                </BatchLabels>
              );
            }

            if (action === 'export') {
              const exportMutationState = getExportMutationState(
                [annotation],
                queueExportSettings
              );

              return (
                <ExportMenu
                  key={action}
                  exportMutationState={exportMutationState}
                  onDownload={format =>
                    annotationActions.download({
                      selectedAnnotationsIds: [annotation.id],
                      format,
                    })
                  }
                  onExport={
                    exportMutationState.visible
                      ? format =>
                          annotationActions.export({
                            selectedAnnotationsIds: [annotation.id],
                            format,
                          })
                      : undefined
                  }
                  onAsyncDownload={() =>
                    annotationActions.asyncDownload({
                      annotations: [annotation],
                    })
                  }
                  selectedItemsCount={1}
                  nested
                  onParentClose={() => setAnchorEl(null)}
                >
                  {({ isOpen }) => (
                    <MenuItem
                      selected={isOpen}
                      data-cy={`single-row-action-${action}`}
                    >
                      <ListItemText>
                        {intl.formatMessage({
                          id: 'containers.annotationList.selectionPanel.buttons.export',
                        })}
                      </ListItemText>
                      <SvgIcon fontSize="small" sx={{ ml: 1 }}>
                        <IconChevronRight />
                      </SvgIcon>
                    </MenuItem>
                  )}
                </ExportMenu>
              );
            }

            if (
              (action === 'reExtract' || action === 'reAutomate') &&
              isReAutomateEnabled &&
              annotationActions
            ) {
              const shouldRenderTrigger = canReExtract
                ? action === 'reExtract'
                : action === 'reAutomate';

              if (!shouldRenderTrigger) {
                return null;
              }

              return (
                <ReProcessMenu
                  key="reProcess"
                  onReExtract={
                    canReExtract
                      ? () => {
                          setAnchorEl(null);
                          annotationActions.reExtract({
                            annotations: [annotation],
                          });
                        }
                      : undefined
                  }
                  onReAutomate={
                    canReAutomate
                      ? () => {
                          setAnchorEl(null);
                          annotationActions.reAutomate({
                            annotations: [annotation],
                          });
                        }
                      : undefined
                  }
                  nested
                >
                  {({ isOpen }) => (
                    <MenuItem
                      selected={isOpen}
                      data-cy="single-row-action-reProcess"
                    >
                      <ListItemText>
                        {intl.formatMessage({
                          id: 'components.annotationOverview.actionButtons.reProcess',
                        })}
                      </ListItemText>
                      <SvgIcon fontSize="small" sx={{ ml: 1 }}>
                        <IconChevronRight />
                      </SvgIcon>
                    </MenuItem>
                  )}
                </ReProcessMenu>
              );
            }

            if (action === 'documentHistory') {
              return (
                <MenuItem
                  key={action}
                  data-cy={`single-row-action-${action}`}
                  onClick={() => {
                    setAnchorEl(null);
                    handleSelectAnnotation({
                      annotationUrl: annotation.url,
                      view: 'documentHistory',
                    });
                  }}
                >
                  <ListItemText>
                    {intl.formatMessage({
                      id: 'features.documentHistory.openRowAction',
                    })}
                  </ListItemText>
                </MenuItem>
              );
            }

            if (action === 'toggleTraining') {
              const isTrainingEnabled = annotation.training_enabled ?? true;
              return (
                <MenuItem
                  key={action}
                  data-cy={`single-row-action-${action}`}
                  onClick={() => {
                    setAnchorEl(null);
                    annotationActions[action]({
                      annotations: [annotation],
                    });
                  }}
                >
                  <ListItemText>
                    {intl.formatMessage({
                      id: isTrainingEnabled
                        ? 'components.annotationOverview.actionButtons.toggleTraining.exclude'
                        : 'components.annotationOverview.actionButtons.toggleTraining.include',
                    })}
                  </ListItemText>
                </MenuItem>
              );
            }

            return (
              <MenuItem
                key={action}
                data-cy={`single-row-action-${action}`}
                onClick={() => {
                  setAnchorEl(null);
                  annotationActions[action]({ annotations: [annotation] });
                }}
              >
                <ListItemText>
                  {intl.formatMessage({
                    id: `components.annotationOverview.actionButtons.${
                      action === 'validate' ? 'open' : action
                    }`,
                  })}
                </ListItemText>
              </MenuItem>
            );
          })}
        </Menu>
      ) : null}
    </>
  );
};

export { RowActions };
